import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { showMessage } from 'dashboard/redux/actions/messages';
import { clearCaseAssignmentPolicy } from 'dashboard/redux/slices/case-assignment-policy';
import { RootState } from 'dashboard/redux/store';
import {
  loadCaseAssignmentPolicy,
  updateCaseAssignmentPolicy,
} from 'dashboard/redux/thunk-actions/case-assignment-policy';
import { loadCaseQueuesList } from 'dashboard/redux/thunk-actions/case-queues';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardCaseAssignmentPolicyShowView from 'dashboard/views/DashboardCaseAssignmentPolicyShowView';
import { EditCaseAssignmentPolicyFormData } from 'dashboard/views/DashboardCaseAssignmentPolicyShowView/FormEditCaseAssignmentPolicy';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { caseAssignmentPolicyId: string };

const DashboardCaseAssignmentPolicyRoute = (props: PropsFromRedux) => {
  useOnMountAndUnmount(props.clearCaseAssignmentPolicy);

  const { caseAssignmentPolicyId } = useParams<Params>() as Params; // caseAssignmentPolicyId is always defined
  const hasUserViewPermission = usePermission(Permission.UserView);
  const hasTeamViewPermission = usePermission(Permission.TeamView);

  const { loading: loadingCaseAssignmentPolicy } = useApiLoader({
    loader: () => props.loadCaseAssignmentPolicy(caseAssignmentPolicyId),
    deps: [props.loadCaseAssignmentPolicy, caseAssignmentPolicyId],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        switch (error.status) {
          case 401:
            props.showMessage(
              'Your role does not have permissions to view case assignment policies in this organization.',
              MessageType.Notice,
            );
            break;
          default:
            props.showMessage('Failed to load case assignment policy.', MessageType.Failure);
        }
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    skip: !hasUserViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    skip: !hasTeamViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadCaseTemplateBasicsOptions(),
    deps: [props.loadCaseTemplateBasicsOptions],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () =>
      props.loadCaseQueuesList({
        currentPage: 1,
        pageSize: 100,
        sortParams: { sort: '-', shouldSortDescending: false },
        filters: { status: ['active'] },
      }),
    deps: [props.loadCaseQueuesList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case queues.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    loading: updateCaseAssignmentPolicyLoading,
    error: updateCaseAssignmentPolicyError,
    callback: updateCaseAssignmentPolicy,
  } = useApiCallback(
    async (values: EditCaseAssignmentPolicyFormData) => {
      if (props.caseAssignmentPolicy == null) {
        props.showMessage(
          'Failed to update case assignment policy, please try again.',
          MessageType.Failure,
          false,
        );
        return;
      }

      const newAssignmentPolicy = {
        ...props.caseAssignmentPolicy,
        attributes: {
          ...props.caseAssignmentPolicy.attributes,
          name: values.name,
          'assignment-order-by': values.assignmentOrderBy,
          'distribution-type': values.distributionType,
          'max-capacity': values.maxCapacity,
          'allowed-user-availabilities': values.allowedUserAvailabilities,
          'unassign-user-availabilities': values.unassignUserAvailabilities,
          'assignee-user-ids': values.userIds,
          'assignee-team-ids': values.teamIds,
          'case-source-list': {
            'source-type': values.caseSourceType,
            'source-ids': values.caseSourceIds,
          },
        },
      };

      await props.updateCaseAssignmentPolicy(props.caseAssignmentPolicy.id, newAssignmentPolicy);
      props.showMessage('Successfully updated assignment policy.', MessageType.Success);
    },
    [props.updateCaseAssignmentPolicy, props.caseAssignmentPolicy],
  );

  useApiErrorHandler({
    error: updateCaseAssignmentPolicyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update case assignment policy, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  if (loadingCaseAssignmentPolicy) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else if (props.caseAssignmentPolicy == null) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that Assignment Policy.'
          backRoute='/dashboard/case-assignment-policies'
        />
      </Backdrop>
    );
  }

  return (
    <DashboardCaseAssignmentPolicyShowView
      currentEnvironment={props.currentEnvironment}
      submitting={updateCaseAssignmentPolicyLoading}
      caseAssignmentPolicy={props.caseAssignmentPolicy}
      users={props.users.filter((user) => user.attributes.status === 'active')}
      teams={props.teams.filter((team) => team.attributes.status === 'active')}
      caseTemplateBasicsOptions={props.caseTemplateBasicsOptions.filter(
        (ct) => ct.status === 'active',
      )}
      caseQueues={props.caseQueues}
      updateCaseAssignmentPolicy={updateCaseAssignmentPolicy}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    caseAssignmentPolicy: state.caseAssignmentPolicy.caseAssignmentPolicy,
    users: state.user.users,
    teams: state.teams.teams,
    caseTemplateBasicsOptions: state.caseTemplates.basicsOptions,
    caseQueues: state.caseQueues.caseQueues,
  };
};

const mapDispatchToProps = {
  loadCaseAssignmentPolicy,
  clearCaseAssignmentPolicy,
  loadUserList,
  loadTeams,
  loadCaseTemplateBasicsOptions,
  loadCaseQueuesList,
  updateCaseAssignmentPolicy,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseAssignmentPolicyRoute);
